import React, { FC } from 'react';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    tableCellClasses,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { getFlagUrl } from '../../utils/getFlagUrl';

type ResultsTableProps = {
    values: string[][];
};

const MERGED_COLUMNS = [0, 1];

const getRidersCount = (values: string[][]): number => {
    let count = 1;
    for (let i = 0; i < values.length; i++) {
        if (values[i][0] === '') {
            count++;
        }
        if (count > 1 && values[i][0] !== '') {
            break;
        }
    }
    return count;
};

export const MultipleRidersTable: FC<ResultsTableProps> = ({ values }) => {
    const [headers, ...rows] = values;

    const countryColIndex = headers.findIndex((h) => h.toLowerCase() === 'country');
    const qColIndex = headers.findIndex((h) => h.toLowerCase() === 'q');
    const pointsIndex = headers.findIndex((h) => h.toLowerCase().includes('points'));

    const isAbandoned = (value?: string) => value === 'ABD' || value === 'DNF' || value === 'DNS';
    const isQualified = (value?: string) => value === 'Q';

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const teams = [];
    const riders = [];
    for (let i = 0; i < rows.length; i += 2) {
        teams.push([rows[i], rows[i + 1]]);
    }
    const ridersCount = getRidersCount(rows);

    for (let i = 0; i < rows.length; i += ridersCount) {
        const tmp = [];
        for (let j = 0; j < ridersCount; j++) {
            tmp.push(rows[i + j]);
        }
        riders.push(tmp);
    }

    if (pointsIndex !== -1) {
        MERGED_COLUMNS.push(pointsIndex);
    }

    // =========================================================
    // 📱 MOBILE VIEW
    // =========================================================
    if (isMobile) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, marginTop: 2, marginBottom: 2 }}>
                {riders.map((riders, index) => {
                    if (!riders) return null;

                    const rank = riders[0][0];
                    const bib = riders[0][1];
                    const hasPointsCol = headers.findIndex((h) => h.toLowerCase().includes('points')) !== -1;

                    return (
                        <Paper
                            key={index}
                            sx={{
                                p: 2,
                                borderRadius: 3,
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 1.5,
                                }}
                            >
                                {/* Rank */}
                                <Typography
                                    fontWeight={700}
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        textAlign: 'center',
                                    }}
                                >
                                    {rank === 'DNF' ? '' : '# '}
                                    {rank}
                                    <Typography
                                        sx={{
                                            fontSize: '0.9rem',
                                            lineHeight: 1.1,
                                            pt: 3,
                                        }}
                                    >
                                        {bib}
                                    </Typography>
                                </Typography>

                                {/* Names */}
                                <Box
                                    sx={{
                                        flex: 1,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 0.5,
                                    }}
                                >
                                    {/* Riders */}
                                    {riders.map((rider, i) => (
                                        <>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    p: 0.5,
                                                    borderRadius: 2,
                                                }}
                                                key={i}
                                            >
                                                <Box>
                                                    <Typography fontWeight={600} sx={{ lineHeight: 1.1 }}>
                                                        {rider[2]}
                                                    </Typography>
                                                    <Typography
                                                        sx={{
                                                            fontSize: '0.75rem',
                                                            pl: 1.5,
                                                            color: 'text.secondary',
                                                            lineHeight: 1.1,
                                                        }}
                                                    >
                                                        {rider[3]}
                                                    </Typography>
                                                </Box>

                                                <img
                                                    src={getFlagUrl(rider[countryColIndex])}
                                                    alt={rider[countryColIndex]}
                                                    className="flag-icon"
                                                    style={{ width: '24px', height: '17px' }}
                                                />
                                            </Box>
                                        </>
                                    ))}
                                </Box>

                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'space-between',
                                    }}
                                >
                                    {riders.map((rider, j) => (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.5,
                                                justifyContent: !hasPointsCol ? 'space-between' : 'end',
                                            }}
                                            key={j}
                                        >
                                            {rider.map((item, k) => {
                                                if (k <= countryColIndex || (hasPointsCol && j > 0)) {
                                                    return null;
                                                }
                                                return (
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 0.5,
                                                            minWidth: hasPointsCol ? 55 : 80,
                                                            textAlign: 'right',
                                                        }}
                                                        key={k}
                                                    >
                                                        <Typography
                                                            sx={{
                                                                fontWeight: 700,
                                                            }}
                                                        >
                                                            {item} {!hasPointsCol ? '' : 'pts'}
                                                        </Typography>
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        </Paper>
                    );
                })}
            </Box>
        );
    }

    return (
        <TableContainer
            component={Paper}
            sx={{
                overflowX: 'auto',
                borderRadius: 2,
                boxShadow: 3,
                marginTop: 2,
                marginBottom: 2,
            }}
        >
            <Table stickyHeader size="small">
                <TableHead>
                    <TableRow>
                        {headers.map((header, index) => (
                            <TableCell
                                key={index}
                                align={'center'}
                                sx={{
                                    fontWeight: 700,
                                    textTransform: 'uppercase',
                                    fontSize: '0.75rem',
                                    backgroundColor: 'grey.900',
                                    color: 'common.white',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {header}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>

                <TableBody>
                    {rows.map((row, rowIndex) => {
                        const isGroupStart = rowIndex % ridersCount === 0; // Rnk column

                        return (
                            <TableRow
                                key={rowIndex}
                                sx={{
                                    [`& .${tableCellClasses.root}`]: {
                                        borderBottom: (theme) =>
                                            (rowIndex + 1) % ridersCount !== 0 || rowIndex === rows.length - 1
                                                ? ''
                                                : theme.palette.mode === 'light'
                                                  ? 'solid black 2px'
                                                  : 'solid white 2px',
                                    },
                                }}
                            >
                                {headers.map((_, cellIndex) => {
                                    const cellValue = row[cellIndex];
                                    const abandoned = isAbandoned(cellValue);
                                    const qualified = cellIndex === qColIndex && isQualified(cellValue);

                                    const isMergeColumn = MERGED_COLUMNS.includes(cellIndex); // Rnk, Bib, Points

                                    // 👉 MERGED CELLS
                                    if (isMergeColumn) {
                                        // skip rendering in second row
                                        if (!isGroupStart) return null;

                                        return (
                                            <TableCell
                                                key={cellIndex}
                                                align="center"
                                                rowSpan={isGroupStart ? ridersCount : 1}
                                                sx={{
                                                    verticalAlign: 'middle',
                                                    fontWeight: cellIndex === 0 ? 600 : 400,
                                                    borderBottom: (theme) =>
                                                        rowIndex === rows.length - ridersCount
                                                            ? 'none'
                                                            : theme.palette.mode === 'light'
                                                              ? 'solid black 2px'
                                                              : 'solid white 2px',
                                                }}
                                            >
                                                {cellValue ?? '—'}
                                            </TableCell>
                                        );
                                    }

                                    // 👉 QUALIFIED BADGE (unchanged)
                                    if (qualified) {
                                        return (
                                            <TableCell key={cellIndex} align="center">
                                                <span className="qualified-badge">Q</span>
                                            </TableCell>
                                        );
                                    }

                                    // 👉 COUNTRY WITH FLAG (unchanged)
                                    if (cellIndex === countryColIndex && cellValue) {
                                        return (
                                            <TableCell
                                                key={cellIndex}
                                                align="center"
                                                sx={{
                                                    border: 'none',
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        gap: 1,
                                                        fontWeight: abandoned ? 600 : 400,
                                                    }}
                                                >
                                                    <img
                                                        src={getFlagUrl(cellValue)}
                                                        alt={cellValue}
                                                        className="flag-icon"
                                                    />
                                                </Box>
                                            </TableCell>
                                        );
                                    }

                                    // 👉 DEFAULT CELL (unchanged)
                                    return (
                                        <TableCell
                                            key={cellIndex}
                                            align="center"
                                            sx={{
                                                fontWeight: abandoned || cellIndex === 0 ? 600 : 400,
                                                border: 'none',
                                            }}
                                        >
                                            {cellValue ?? '—'}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
};
